import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import AssessmentFilters from '@app/src/pages/ResourceCollection/Filters/AllAssessmentsFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/index'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { Box, Grid } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router-dom'
import AssessmentOverviewGraphWithHeader from './AssessmentOverviewGraphWithHeader'

type AllAssessmentsOverviewSceneProps = {
  allowedFilters: string[]
  activatedAssessmentTemplates: AssessmentTemplate[]
  assessedPeriodNames: string[]
} & Pick<ResourceCollectionSceneProps, 'actionButtons' | 'tabs'>

const AllAssessmentsOverviewScene: React.FC<AllAssessmentsOverviewSceneProps> = ({
  allowedFilters,
  activatedAssessmentTemplates,
  assessedPeriodNames,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const hasWriteAccess = useWriteAccess()

  const {
    facets: [assessmentFacets = []],
  } = useFetchFacets({
    key: [FetchKey.AssessmentFacets, 'periods'],
    endpoint: endpoints.assessmentFacet,
    facetsParam: [{ name: 'assessmentTemplateId', isEnum: true }],
    filter: userFilters,
    options: { enabled: Boolean(userFilters), staleTime: ONE_MINUTE_MS },
  })

  const assessmentTemplateIdFilterValue = useMemo(() => {
    return (
      (userFilters?.find(group => group.name === 'assessmentTemplate.id')?.filters[0]?.value as string[]) ?? []
    ).map(Number)
  }, [userFilters])

  const filteredAssessmentTemplates = useMemo(() => {
    return activatedAssessmentTemplates.filter(template => assessmentTemplateIdFilterValue.includes(template.id))
  }, [assessmentFacets, activatedAssessmentTemplates])

  return (
    <ResourceCollectionScene
      enableScroll
      title={formatMessage({ id: 'navbar.assessments' })}
      filter={
        <AssessmentFilters
          allowedFilters={allowedFilters}
          preSelectedAssessmentTemplateIds={activatedAssessmentTemplates.map(x => x.id)}
          preSelectedPeriodNames={assessedPeriodNames}
        />
      }
      actionButtons={
        hasWriteAccess
          ? [
              {
                label: formatMessage({ id: 'assessments.setAssessments' }),
                variant: 'outlined',
                to: generatePath(paths.configurations, {
                  configurationsPage: CONFIGURATION_PAGE_IDS.Assessments,
                }),
              },
            ]
          : undefined
      }
      disableLeftPadding
      {...props}
    >
      <Box px={4} my={3}>
        <Grid container spacing={2}>
          {filteredAssessmentTemplates.map(assessmentTemplate => (
            <Grid item xs={12} md={6} key={assessmentTemplate.id}>
              <AssessmentOverviewGraphWithHeader assessmentTemplate={assessmentTemplate} userFilters={userFilters} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </ResourceCollectionScene>
  )
}

export default AllAssessmentsOverviewScene
